import React, { useEffect, useRef, useState } from 'react';

/**
 * AnimatedCounter — counts from 0 to `end` when scrolled into view.
 * If `end` is not numeric (e.g. "AA+" or "BIO"), simply displays it without animation.
 */
const AnimatedCounter = ({ value, duration = 1600, suffix = '', prefix = '', className = '' }) => {
  const ref = useRef(null);
  const [display, setDisplay] = useState('');

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Extract numeric part (supports "20+", "500+", "100%", "5.0")
    const match = String(value).match(/^(-?\d+(?:\.\d+)?)(.*)$/);
    if (!match) {
      setDisplay(value);
      return;
    }
    const endNum = parseFloat(match[1]);
    const tail = match[2];
    const isFloat = match[1].includes('.');

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        const startTime = performance.now();
        const step = (now) => {
          const t = Math.min(1, (now - startTime) / duration);
          const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
          const curr = endNum * eased;
          setDisplay((isFloat ? curr.toFixed(1) : Math.round(curr)) + tail);
          if (t < 1) requestAnimationFrame(step);
          else setDisplay(match[1] + tail);
        };
        requestAnimationFrame(step);
      },
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [value, duration]);

  return (
    <span ref={ref} className={className}>
      {prefix}{display || (typeof value === 'string' && !/\d/.test(value) ? value : '0')}{suffix}
    </span>
  );
};

export default AnimatedCounter;
